import React from 'react';
import styled from '@emotion/styled';
import { css, SerializedStyles, Theme } from '@emotion/react';
import { rem } from 'polished';
import { Link } from '../../primitives';
import { mixins, spaces } from '../../core/styles/theme/default';
import { LinkVariant } from '../../primitives/Link/Link';
import { ImageInterface } from '~/types';

const StyledTeaserNavigation = styled.aside`
  display: block;
  position: relative;
`;

const StyledLink = styled(Link)(
  ({ theme }: { theme?: Theme }) => css`
    justify-content: flex-start;
    span {
      font-size: ${rem(16)};
      line-height: ${rem(16)};

      ${mixins.MQ('medium')} {
        font-size: ${rem(20)};
        line-height: ${rem(20)};
      }
    }

    .m-richtext & {
      text-decoration: none;
    }

    ${theme?.stylesExtension?.teaserNavigation?.link}
  `,
);

const StyledGrid = styled.div(
  ({ customGrid, theme }: { customGrid?: SerializedStyles; theme?: Theme }) =>
    css`
      display: grid;
      grid-auto-rows: 1fr;

      ${mixins.responsiveStyles({
        'grid-template-columns': {
          zero: 'repeat(1, 1fr)',
          small: 'repeat(2, 1fr)',
          large: 'repeat(4, 1fr)',
        },
        'grid-gap': {
          zero: rem(spaces.alpha),
          medium: rem(spaces.delta),
          ultra: rem(spaces.epsilon),
        },
      })}

      ${customGrid ?? null}

    ${theme?.stylesExtension?.teaserNavigation?.grid}
    `,
);

const scrollToSection = (e: any, hash: string) => {
  e.preventDefault();

  const element = document.getElementById(hash.replace('#', ''));

  if (element) {
    const headerHeight =
      document.getElementById('main-header')?.offsetHeight ?? 0;

    window.scrollTo({
      top:
        element.getBoundingClientRect().top +
        window.scrollY -
        headerHeight -
        20,
      behavior: 'smooth',
    });
  }
};

const TeaserNavigation = (teaserNavigationProps: TeaserNavigationProps) => {
  if (!teaserNavigationProps) return null;

  const {
    items,
    variant = 'emphasizedImageSmall',
    customGrid,
  } = teaserNavigationProps;

  return (
    <StyledTeaserNavigation>
      <StyledGrid customGrid={customGrid}>
        {items.map((data) => (
          <StyledLink
            key={data.href}
            linkText={data.navigationTitle}
            link={{
              href: data.href,
              title: data.navigationTitle,
            }}
            variant={variant}
            teaserIcon={data.teaserIcon}
            backgroundImage={data.backgroundImage}
            {...(data.href.startsWith('#') && {
              onClick: (e) => scrollToSection(e, data.href),
            })}
          />
        ))}
      </StyledGrid>
    </StyledTeaserNavigation>
  );
};

export type TeaserNavigationProps = {
  items: {
    href: string;
    navigationTitle: string;
    teaserIcon?: ImageInterface | null;
    backgroundImage?: ImageInterface | null;
  }[];
  variant?: LinkVariant;
  customGrid?: SerializedStyles;
} | null;

export default TeaserNavigation;
